import { IPresentationModel } from '@src/slide/domain/ui-model/element-modeles/Interface/presentation-model.interface';
import {
    FirstAlignArea,
} from '@src/slide/application/service/slide-content-layout-analyzer/core/algorithm/data-structure/first-align-area';
import { VTitleTemplate } from './data-structure/vertical-title-template';
import { ContentTree, ContentTreeNodeType } from './data-structure/content-tree';
import {
    VisualBlock,
} from '@src/slide/application/service/slide-content-layout-analyzer/core/algorithm/data-structure/visual-block';
import {
    checkHorAlignSame,
    checkRectAlign, checkVerAlignSame,
    RectHorizontalAlignType,
    RectVerticalAlignType,
} from '@src/slide/infrastructure/module/independent/graphic/rect/rect';

export interface IVisualBlocksSimilar {
    similarBlock: VisualBlock,
    horizontalAlignType?: RectHorizontalAlignType,
    verticalAlignType?: RectVerticalAlignType,
}

export interface ITitleResult {
    titleTemplateMap: Map<VTitleTemplate, Array<FirstAlignArea>>,
    titleTemplateSimilarMap: Map<VTitleTemplate, Array<number>>,
    titleTemplateSimilarBlocksMap: Map<number, Array<IVisualBlocksSimilar>>;
}

export class TitleAnalyzer {
    // 第一个
    private firstAlignAreas: Array<FirstAlignArea>;
    private vTitleMaybeModes: Map<number, Array<FirstAlignArea>> = new Map();
    // 模板和对应区域的映射
    private vTitleTemplateMap: Map<VTitleTemplate, Array<FirstAlignArea>> = new Map();
    // 已经成为模板的页面的映射
    private vTitleTemplateIndexMap: Map<number, VTitleTemplate> = new Map();
    // 标题模板的相似页面们
    private vTitleTemplateSimilarityMap: Map<VTitleTemplate, Array<number>> = new Map();
    // 页对应的block数组
    private vTitleTemplateSimilarBlocksMap: Map<number, Array<IVisualBlocksSimilar>> = new Map();
    // 提取出有重复区域的节点，可能为标题模板
    private vTitleMaybeModesMap: Map<number, Array<FirstAlignArea>> = new Map();

    // 根据布局信息推测
    public analysisByLayout(
        presentation: IPresentationModel,
        contentTreeList: Array<ContentTree>,
    ): ITitleResult {
        this.reset();
        // 提取有用信息
        this.getInfoFromContentTree(presentation, contentTreeList);
        // 先看vTitleMaybeModes里的标题模版是否合理
        this.checkTitleModes(presentation);
        // 留下合理的模版，对比其他页面是否和已经存在的模版比较接近
        this.analysisAllSlide(presentation, contentTreeList);
        const result = {
            titleTemplateMap: this.vTitleTemplateMap,
            titleTemplateSimilarMap: this.vTitleTemplateSimilarityMap,
            titleTemplateSimilarBlocksMap: this.vTitleTemplateSimilarBlocksMap,
        };
        return result;
    }

    public reset() {
        this.firstAlignAreas = [];
        this.vTitleMaybeModes = new Map();
        this.vTitleMaybeModesMap = new Map();
        this.vTitleTemplateMap = new Map();
        this.vTitleTemplateIndexMap = new Map();
        this.vTitleTemplateSimilarityMap = new Map();
        this.vTitleTemplateSimilarBlocksMap = new Map<number, Array<IVisualBlocksSimilar>>();
    }

    private getInfoFromContentTree(
        presentation: IPresentationModel,
        contentTreeList: Array<ContentTree>,
    ) {
        const firstNodes = contentTreeList.map(tree => tree.getFirstHOrHNode());
        const rects = firstNodes.map((node) => {
            const rect = node?.getRect();
            if (!rect) return rect;
            if (node?.parent?.type === ContentTreeNodeType.Vertical) {
                rect.left = 0;
                rect.width = presentation.props.slideWidth;
            }
            if (node?.parent?.type === ContentTreeNodeType.Horizontal) {
                rect.top = 0;
                rect.height = presentation.props.slideHeight;
            }
            return rect;
        });
        const firstAlignAreas = firstNodes.map((node, index) => new FirstAlignArea(index, node, rects[index]));
        // eslint-disable-next-line complexity
        firstAlignAreas.sort((a, b) => {
            if (!a.node && !b.node) return 0;
            if (!a.node || !b.node) {
                if (a.node) return -1;
                if (b.node) return 1;
                return 0;
            }
            if (a.node.parent?.type !== b.node.parent?.type) {
                return Number(b.node.parent?.type) - Number(a.node.parent?.type);
            }
            if (a.node.parent?.type === ContentTreeNodeType.Vertical) {
                if (Math.abs((Number(a.area?.top) + Number(a.area?.height))
                    - (Number(b.area?.top) + Number(b.area?.height))) < 1) {
                    return Number(a.area?.top) - Number(b.area?.top);
                }
                return (Number(a.area?.top) + Number(a.area?.height))
                    - (Number(b.area?.top) + Number(b.area?.height));
            }
            if (a.node.parent?.type === ContentTreeNodeType.Horizontal) {
                if (Math.abs((Number(a.area?.left) + Number(a.area?.width))
                    - (Number(b.area?.left) + Number(b.area?.width))) < 1) {
                    return Number(a.area?.left) - Number(b.area?.left);
                }
                return (Number(a.area?.left) + Number(a.area?.width))
                    - (Number(b.area?.left) + Number(b.area?.width));
            }
            return Number(a.area?.top) - Number(b.area?.top);
        });

        // const hTitleMaybeModes: Map<number, Array<FirstAlignArea>> = new Map();
        let i = 0;
        while (i < firstAlignAreas.length
        && (firstAlignAreas[i].type === ContentTreeNodeType.Vertical
           || firstAlignAreas[i].type === ContentTreeNodeType.None)) {
            const currentArea = firstAlignAreas[i];
            if (currentArea.hasText()) {
                const areaHeight = Math.floor(Number(currentArea.area?.top) + Number(currentArea.area?.height));
                if (this.vTitleMaybeModesMap.has(areaHeight)) {
                    this.vTitleMaybeModesMap.get(areaHeight)?.push(currentArea);
                } else {
                    this.vTitleMaybeModesMap.set(areaHeight, [currentArea]);
                }
            }
            i += 1;
        }
        this.vTitleMaybeModesMap.forEach((areas, key) => {
            if (areas.length > 1) {
                this.vTitleMaybeModes.set(key, areas);
            }
        });
        this.firstAlignAreas = firstAlignAreas.sort((a, b) => a.slideIndex - b.slideIndex);
    }

    /**
     * vTitleMaybeModes里的标题模版是否合理
     * @private
     */
    // eslint-disable-next-line complexity
    private checkTitleModes(presentation: IPresentationModel) {
        // 内部检测是否高度一样的内容也一样
        const keys = this.vTitleMaybeModes.keys();
        for (const key of keys) {
            const areas = this.vTitleMaybeModes.get(key);
            if (!areas) continue;
            let sameArea;
            let sameIndex;
            let sameAlignResult;
            for (let i = 0; i < areas.length; i++) {
                const current = areas[i];
                if (!current.node) continue;
                for (let j = i + 1; j < areas.length; j++) {
                    const compareNode = areas[j].node;
                    if (!compareNode) continue;
                    // 对比高度一样的是否有布局一样的
                    if (current.node?.isSameStructure(compareNode)) {
                        sameArea = current;
                        sameIndex = i;
                        const otherArea = areas[j].area;
                        if (current.area && otherArea) {
                            sameAlignResult = checkRectAlign(current.area, otherArea, 1);
                        }
                        break;
                    }
                }
                if (sameArea) break;
            }
            if (sameArea && sameAlignResult.result) {
                const equalList: Array<FirstAlignArea> = [sameArea];
                for (let i = 0; i < areas.length; i++) {
                    if (i !== sameIndex
                        && areas[i].node?.isSameStructure(sameArea.node)) {
                        const currentRect = areas[i].area;
                        if (!currentRect || !sameArea.area) continue;
                        const currentAlignResult = checkRectAlign(currentRect, sameArea.area, 1);
                        if (!currentAlignResult.result) continue;
                        if (checkHorAlignSame(
                            currentAlignResult.horizontalAlignType,
                            sameAlignResult.horizontalAlignType,
                        ) && checkVerAlignSame(
                            currentAlignResult.verticalAlignType,
                            sameAlignResult.verticalAlignType,
                        )) {
                            equalList.push(areas[i]);
                        }
                    }
                }
                const template = new VTitleTemplate(key, sameArea);
                template.verAlignType = sameAlignResult.verticalAlignType;
                template.horAlignType = sameAlignResult.horizontalAlignType;
                this.vTitleTemplateMap.set(template, equalList);
            } else {
                // 没有找到一样的，说明不成立
                this.vTitleMaybeModes.delete(key);
            }
        }
        // 检测模版之间是否重复或者相似，进而进行合并
        const templateKeys = Array.from(this.vTitleTemplateMap.keys());
        let i = 0;
        while (i < templateKeys.length) {
            const currentTemplate = templateKeys[i];
            const currentEqualList = this.vTitleTemplateMap.get(currentTemplate);
            for (let j = templateKeys.length - 1; j > i; j--) {
                const otherTemplate = templateKeys[j];
                const otherEqualList = this.vTitleTemplateMap.get(otherTemplate);
                if (!currentTemplate.templateArea.node
                    || !otherTemplate.templateArea.node) continue;
                if (currentTemplate.templateArea.node?.isSameStructure(otherTemplate.templateArea.node)) {
                    if (currentEqualList && otherEqualList) {
                        currentEqualList?.push(...otherEqualList);
                    }
                    this.vTitleTemplateMap.delete(otherTemplate);
                    templateKeys.splice(j, 1);
                } else if (currentTemplate.templateArea.node?.isSimilarStructure(otherTemplate.templateArea.node)) {
                    this.vTitleTemplateMap.delete(otherTemplate);
                    templateKeys.splice(j, 1);
                }
            }
            i += 1;
        }
        // 过滤不符合条件的结果
        this.filterResult(presentation);
        this.vTitleTemplateMap.forEach((equalList, template) => {
            for (let i = 0; i < equalList.length; i++) {
                this.vTitleTemplateIndexMap.set(equalList[i].slideIndex, template);
            }
        });
    }

    // 留下合理的模版，对比其他页面是否和已经存在的模版比较接近
    // eslint-disable-next-line complexity
    private analysisAllSlide(
        presentation: IPresentationModel,
        contentTreeList: Array<ContentTree>,
    ) {
        // 遍历所有页面
        for (let i = 0; i < contentTreeList.length; i++) {
            const contentTree = contentTreeList[i];
            // 看每一页和已经存在的模版比较
            const templateKeys = Array.from(this.vTitleTemplateMap.keys());
            const slideTemplate = this.vTitleTemplateIndexMap.get(i);
            for (let j = 0; j < templateKeys.length; j++) {
                const template = templateKeys[j];
                const sameAreas = this.vTitleTemplateMap.get(template);
                if (!sameAreas) continue; // 已经被合并到其他模版了
                if (slideTemplate === template) continue; // 就是一个，不需要比了
                if (this.compareTreeHasSameTemplate(template, contentTree)) {
                    // 能找到模板相同的内容
                    this.vTitleTemplateMap.get(template)?.push(this.firstAlignAreas[i]);
                    this.vTitleTemplateIndexMap.set(i, template);
                    break;
                }
                const { isSimilar, similarInfos } = this.compareTemplateAndTreeIsSimilar(
                    template,
                    contentTree,
                    presentation,
                );
                // 找到了相似模版
                if (isSimilar) {
                    let currentSimilar = this.vTitleTemplateSimilarityMap.get(template);
                    if (similarInfos) {
                        this.vTitleTemplateSimilarBlocksMap.set(i, similarInfos);
                    }
                    // 没有模版，加入到相似模版Map里
                    if (!currentSimilar) {
                        currentSimilar = [i];
                        this.vTitleTemplateSimilarityMap.set(template, currentSimilar);
                    } else {
                        if (!currentSimilar.includes(i)) {
                            currentSimilar.push(i);
                        }
                    }
                    if (slideTemplate) {
                        // 页面的模板和遍历到的模板不是一个，并且相似，那么应该将模板合并为同一个。
                        const value = this.vTitleTemplateMap.get(slideTemplate);
                        value?.forEach((area) => {
                            if (!currentSimilar?.includes(area.slideIndex)) {
                                currentSimilar?.push(area.slideIndex);
                            }
                        });
                        this.vTitleTemplateMap.delete(slideTemplate);
                        const similar = this.vTitleTemplateSimilarityMap.get(slideTemplate);
                        similar?.forEach((index) => {
                            if (!currentSimilar?.includes(index)) {
                                currentSimilar?.push(index);
                            }
                        });
                        this.vTitleTemplateSimilarityMap.delete(slideTemplate);
                    }
                }
            }
        }
    }

    /**
     * 比较两个标题模版相似度
     */
    private compareTemplateAndTreeIsSimilar(
        template: VTitleTemplate,
        contentTree: ContentTree,
        presentation: IPresentationModel,
    ): {
            isSimilar: boolean;
            similarInfos?: Array<IVisualBlocksSimilar>,
        } {
        if (!template.templateArea.node) return { isSimilar: false };
        const templateBlocks = template.templateArea.node?.visualBlocks;
        const similarInfos: Array<IVisualBlocksSimilar> = [];
        for (let i = 0; i < templateBlocks.length; i++) {
            const templateBlock = templateBlocks[i];
            const currentNode = contentTree.children[0];
            if (!currentNode) continue;
            let blockFind = false;
            for (let j = 0; j < currentNode.visualBlocks.length; j++) {
                const slideBlock = currentNode.visualBlocks[j];
                if (templateBlock.isSimilarToOther(slideBlock, undefined, presentation.props.slideWidth / 2)) {
                    const similarInfo: IVisualBlocksSimilar = { similarBlock: slideBlock };
                    const alignType = checkRectAlign(slideBlock.rect, templateBlock.rect, 1);
                    if (alignType.result && alignType.horizontalAlignType && alignType.verticalAlignType) {
                        similarInfo.horizontalAlignType = alignType.horizontalAlignType;
                        similarInfo.verticalAlignType = alignType.verticalAlignType;
                    }
                    similarInfos.push(similarInfo);
                    blockFind = true;
                    break;
                }
            }
            if (!blockFind) return { isSimilar: false };
        }
        return {
            isSimilar: true,
            similarInfos,
        };
    }

    private compareTreeHasSameTemplate(
        template: VTitleTemplate,
        contentTree: ContentTree,
    ): boolean {
        if (!template.templateArea.node) return false;
        const templateBlocks = template.templateArea.node?.visualBlocks;
        const similarBlocks: Array<VisualBlock> = [];
        for (let i = 0; i < templateBlocks.length; i++) {
            const templateBlock = templateBlocks[i];
            const currentNode = contentTree.children[0];
            if (!currentNode) continue;
            let blockFind = false;
            for (let j = 0; j < currentNode.visualBlocks.length; j++) {
                const slideBlock = currentNode.visualBlocks[j];
                if (templateBlock.isSameToOther(slideBlock)) {
                    const currentAlignResult = checkRectAlign(templateBlock.rect, slideBlock.rect, 1);
                    if (!currentAlignResult.result) continue;
                    if (checkHorAlignSame(
                        currentAlignResult.horizontalAlignType,
                        template.horAlignType,
                    ) && checkVerAlignSame(
                        currentAlignResult.verticalAlignType,
                        template.verAlignType,
                    )) {
                        similarBlocks.push(slideBlock);
                        blockFind = true;
                        break;
                    }
                }
            }
            if (!blockFind) return false;
        }
        return true;
    }

    private filterResult(presentation: IPresentationModel) {
        const pageHeight = presentation.props.slideHeight;
        const templateKeys = Array.from(this.vTitleTemplateMap.keys());
        for (let i = templateKeys.length - 1; i >= 0; i--) {
            const currentTemplate = templateKeys[i];
            if (currentTemplate.height > pageHeight / 2) {
                this.vTitleTemplateMap.delete(currentTemplate);
            }
        }
    }
}
